import React, {
  PropsWithChildren,
  useState,
  ErrorInfo,
  useEffect,
  useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import * as Headers from '../Headers'
import * as Footers from '../Footers'
import * as Navigator from '../Navigator'
import { FlashError } from './FlashError'
import maricopaFooterLogoSrc from '../../svg/maricopa-footer-logo.svg'
import { remCalc } from 'utils'

const CONTENT_VIEW_INLINE_STYLES = {
  height: `calc(100vh - ${remCalc(64)})`,
}

const SECONDARY_DRAWER_PORTAL_ID = 'app-secondary-drawer-root'

// Error boundary component for AppNavigator
class ErrorBoundary extends React.Component<
  { children: React.ReactNode; component: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; component: string }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`Error in ${this.props.component}:`, error)
    console.error(
      `Component stack for ${this.props.component}:`,
      errorInfo.componentStack
    )
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 10, margin: 10, border: '1px solid #f44336' }}>
          <h4>Error in {this.props.component}</h4>
          <p>This component couldn't be rendered due to an error.</p>
          <details style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}>
            <summary>Error details</summary>
            <pre>{this.state.error && this.state.error.toString()}</pre>
          </details>
        </div>
      )
    }

    return this.props.children
  }
}

type RenderFunction = () => JSX.Element | JSX.Element[]
type ComponentProp = React.FC<any> | React.ComponentClass<any>

export interface AppNavigatorProps {
  appearance?: 'primary' | 'secondary'
  renderDrawerItems?: RenderFunction
  renderSearchResults?: RenderFunction
  profileActive?: boolean
  profileColor?: string
  profileName?: string
  profileImage?: string
  searchEnabled?: boolean
  drawerEnabled?: boolean
  menuEnabled?: boolean
  profileNavEnabled?: boolean
  hasDrawer?: boolean
  logo?: ComponentProp
  profileNavigation?: ComponentProp
  onSearchUpdate?: (s: string) => void
  onClickLogo?: () => void
  showFooter?: boolean
  // Drawer state from Redux (new props)
  drawerActive?: boolean
  drawerCompact?: boolean
  toggleDrawer?: (newState?: boolean) => void
}

const AppNavigatorLayout: React.FC<PropsWithChildren<AppNavigatorProps>> = ({
  logo: Logo,
  profileNavigation: ProfileNavigation,
  renderDrawerItems,
  children,
  drawerEnabled = true,
  showFooter = true,
  onSearchUpdate: handleSearchUpdate,
  onClickLogo: handleLogoClick,
  // Get drawer state and toggle from props
  drawerActive = false,
  drawerCompact = false,
  toggleDrawer = () => console.warn('No toggleDrawer function provided'),
  ...props
}) => {
  try {
    const { pathname } = useLocation()
    const [profileActive, setProfileActive] = useState(false)
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    const toggleProfile = (active: boolean) => {
      setProfileActive(active)
    }

    useEffect(() => {
      setProfileActive(false)
    }, [pathname])

    useEffect(() => {
      if (drawerActive && !drawerEnabled) {
        toggleDrawer()
      }
    }, [drawerActive, drawerEnabled, toggleDrawer])

    return (
      <div className="flex flex-col min-h-screen h-auto w-screen lg:h-screen overflow-x-hidden lg:overflow-y-hidden lg:max-h-screen lg:absolute lg:inset-0">
        <ErrorBoundary component="Headers.Navigator">
          <Headers.Navigator
            onToggleDrawer={toggleDrawer}
            profileNavigation={ProfileNavigation}
            drawerActive={drawerActive}
            profileActive={profileActive}
            onToggleProfile={toggleProfile}
            profileName={props.profileName || 'User'}
            profileColor={props.profileColor || '#333333'}
            profileImage={props.profileImage || ''}
            onSearchUpdate={handleSearchUpdate}
            renderResults={props.renderSearchResults}
            logo={Logo}
            appearance={props.appearance}
            searchEnabled={props.searchEnabled}
            menuEnabled={props.menuEnabled}
            profileNavEnabled={props.profileNavEnabled}
            onClickLogo={handleLogoClick}
          />
        </ErrorBoundary>
        <div className="flex flex-shrink">
          {drawerEnabled && drawerActive && renderDrawerItems ? (
            <ErrorBoundary component="Navigator.Drawer">
              <Navigator.Drawer compact={true} push={true}>
                {renderDrawerItems()}
              </Navigator.Drawer>
            </ErrorBoundary>
          ) : (
            <span />
          )}
          <div
            id={SECONDARY_DRAWER_PORTAL_ID}
            data-testid={SECONDARY_DRAWER_PORTAL_ID}
            className="flex flex-shrink-0"
          />
          <div
            ref={scrollContainerRef}
            className="flex flex-grow overflow-auto flex-shrink bg-contentareabackgroundalt"
            style={CONTENT_VIEW_INLINE_STYLES}
          >
            <div
              className={`w-full min-h-full px-3 pt-3 md:px-6 md:pt-6 ${
                showFooter ? 'pb-32' : 'pb-6'
              }`}
            >
              <div
                data-testid="app-content-shell"
                className="flex min-h-full w-full max-w-7xl mx-auto bg-white border border-contentareaborder shadow-sm"
              >
                <div className="w-full min-h-full bg-white">
                  <FlashError>{children}</FlashError>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showFooter ? (
          <ErrorBoundary component="Footers.Standard">
            <Footers.Standard
              footerImageSrc={maricopaFooterLogoSrc}
              absolute={true}
              scrollContainer={scrollContainerRef}
            />
          </ErrorBoundary>
        ) : null}
      </div>
    )
  } catch (error) {
    console.error('AppNavigatorLayout: Unexpected error:', error)
    return (
      <div style={{ padding: 20 }}>
        <h2>Error loading layout</h2>
        <p>There was an error loading the application layout.</p>
        <details>
          <summary>Error details</summary>
          <pre>{String(error)}</pre>
        </details>
      </div>
    )
  }
}

AppNavigatorLayout.displayName = 'AppNavigatorLayout'

export const AppNavigator: React.FC<PropsWithChildren<AppNavigatorProps>> = ({
  children,
  hasDrawer,
  ...props
}) => {
  return (
    <ErrorBoundary component="AppNavigatorLayout">
      <AppNavigatorLayout {...props}>{children}</AppNavigatorLayout>
    </ErrorBoundary>
  )
}

AppNavigator.displayName = 'AppNavigator'
